import type { Seam } from "daedalus-core";
import { CELLS_PER_TILE, SOLID, bandLinks, type BandLink, type BandMapSize, type CollisionGrid } from "odyssey-shared";

/**
 * A neighbour's chunks as the page holds them: a chunk's collision cells, `chunkSize * CELLS_PER_TILE` square in rows, or
 * null for a chunk the page does not hold.
 */
export type BandChunks = (cx: number, cy: number) => Readonly<Uint8Array> | null;

/**
 * One link's neighbour as the band reads it: its size in collision cells and its chunks' cells by index, filled by
 * `refresh`.
 */
interface Across {
  readonly link: BandLink;
  readonly width: number;
  readonly height: number;
  readonly columns: number;
  readonly cells: (Readonly<Uint8Array> | null)[];
}

/**
 * The owning map's collision grid as the page predicts on it (ADR-147 decisions 1, 3 and 18): the map's own cells, and
 * beyond an edge that a seam not `closed` shares with a placed neighbour, along the stretch a crossing may use there (the
 * whole stretch of an `open` seam, the listed spans of a `closed-but` one), the neighbour's own cells, read from the
 * chunks the page holds at any depth, so the character keeps walking onto the neighbour's ground while the crossing is
 * handed over and until the new map's `welcome` moves the frame. The links are the map server's own (`bandLinks`); the
 * server holds only the hand band's two tiles, and the page corrects to it as it does to any snapshot. A chunk the page
 * does not hold reads as a wall, as does every edge with no link and a cell beyond two edges at once. The grid object is
 * made once and its `beyond` reads what the last `refresh` filled, so a predictor holding it needs nothing swapped when
 * a chunk lands, and a read allocates nothing.
 */
export class PageBand {
  public readonly links: readonly BandLink[];
  public readonly grid: CollisionGrid;
  private readonly across: Across[];
  private readonly cellsPerChunk: number;

  /**
   * @param base The owning map's grid.
   * @param owner The owning map's size and place.
   * @param maps Every map of the world manifest.
   * @param seams The manifest's seams.
   * @param chunkSize The world's chunk size, in tiles.
   */
  public constructor(base: CollisionGrid, owner: BandMapSize, maps: readonly BandMapSize[], seams: readonly Seam[], chunkSize: number) {
    this.cellsPerChunk = chunkSize * CELLS_PER_TILE;
    const others = maps.filter((map) => map.id !== owner.id);
    this.links = owner.place === undefined ? [] : bandLinks(owner, others, seams);
    this.across = this.links.map((link) => {
      const map = others.find((other) => other.id === link.neighbour) as BandMapSize;
      const columns = Math.ceil(map.width / chunkSize);
      return { link, width: map.width * CELLS_PER_TILE, height: map.height * CELLS_PER_TILE, columns, cells: new Array<Readonly<Uint8Array> | null>(columns * Math.ceil(map.height / chunkSize)).fill(null) };
    });
    const { width, height, cells, outside } = base;
    this.grid = { width, height, cells, outside, beyond: (cx, cy) => this.read(cx, cy) };
  }

  /**
   * Reads again which of each neighbour's chunks the page holds, as a neighbour's chunk lands or is let go.
   *
   * @param chunksOf A neighbour's chunks by its id, or null when the page holds none of it.
   */
  public refresh(chunksOf: (mapId: string) => BandChunks | null): void {
    for (const across of this.across) {
      const chunks = chunksOf(across.link.neighbour);
      for (let index = 0; index < across.cells.length; index += 1) {
        across.cells[index] = chunks === null ? null : chunks(index % across.columns, Math.floor(index / across.columns));
      }
    }
  }

  /**
   * One cell outside the owning map.
   *
   * @param cx The column, in the owning map's collision cells.
   * @param cy The row.
   * @returns The neighbour's cell there, or `SOLID`.
   */
  private read(cx: number, cy: number): number {
    const { width, height } = this.grid;
    const inColumns = cx >= 0 && cx < width;
    const inRows = cy >= 0 && cy < height;
    if (inColumns === inRows) {
      return SOLID;
    }
    for (let index = 0; index < this.across.length; index += 1) {
      const across = this.across[index] as Across;
      const link = across.link;
      const along = link.axis === "y" ? cx : cy;
      if (link.along[Math.floor(along / CELLS_PER_TILE)] !== 1) {
        continue;
      }
      if (link.side === "south" && cy >= height) {
        return this.cell(across, cx + link.cellShift, cy - height);
      }
      if (link.side === "north" && cy < 0) {
        return this.cell(across, cx + link.cellShift, across.height + cy);
      }
      if (link.side === "east" && cx >= width) {
        return this.cell(across, cx - width, cy + link.cellShift);
      }
      if (link.side === "west" && cx < 0) {
        return this.cell(across, across.width + cx, cy + link.cellShift);
      }
    }
    return SOLID;
  }

  /**
   * One cell of a neighbour, in its own collision cells.
   *
   * @param across The neighbour.
   * @param x The column.
   * @param y The row.
   * @returns The cell, or `SOLID` off the neighbour or in a chunk the page does not hold.
   */
  private cell(across: Across, x: number, y: number): number {
    if (x < 0 || y < 0 || x >= across.width || y >= across.height) {
      return SOLID;
    }
    const per = this.cellsPerChunk;
    const cx = Math.floor(x / per);
    const cy = Math.floor(y / per);
    const cells = across.cells[cy * across.columns + cx];
    return cells === null || cells === undefined ? SOLID : (cells[(y - cy * per) * per + (x - cx * per)] as number);
  }
}
